export type Language = 'en' | 'uk' | 'ja';

export const translations = {
  en: {
    nav: {
      story: 'Story',
      howItWorks: 'How It Works',
      features: 'Features',
      invest: 'Invest',
      contact: 'Contact',
      impact: 'Impact',
    },
    hero: {
      label: 'Veterinary AI Platform',
      headline: 'AI infrastructure for real veterinary clinics',
      subtitle: 'Dr.Vet helps veterinary teams reduce documentation burden, structure clinical workflows, and support better decision-making in everyday practice. Developed in active clinics and refined under demanding conditions.',
      cta: 'Explore the Platform',
      ctaSecondary: 'See How It Works',
      proof: 'Tested in real veterinary practice since 2022.',
    },
    story: {
      badge: 'Real-world case study',
      title: 'Clinical Case Study: VetDopomoga',
      subtitle: 'VetDopomoga clinics, Sumy region, Ukraine — 2022–2025',
      p1: 'VetDopomoga clinics in Sumy region served as the original real-world environment where Dr.Vet’s early workflows were tested and refined. The case demonstrates how AI-supported tools can improve documentation consistency and operational resilience in demanding clinical settings.',
      p2: 'Despite migration, power outages, and operational challenges, the clinics continued to deliver care.',
      p3: 'By 2025, with Dr.Vet AI assistance, documentation time decreased by 15%, examination thoroughness improved by 40%, and the clinics achieved full operational recovery.',
      outcomesLabel: 'Case study outcomes',
      chartVisits: 'Clinic Visits Recovery (2022–2025)',
      chartSales: 'Revenue Recovery (2022–2025)',
      chartMap: 'Sumy Region — War Zone & Recovery Map',
      stat1: '15%',
      stat1Label: 'Faster Documentation',
      stat2: '40%',
      stat2Label: 'More Thorough Examinations',
      stat3: '100%',
      stat3Label: 'Full Operational Recovery despite wartime conditions',
    },
    flowchart: {
      badge: 'Technology',
      title: 'How Dr.Vet Works',
      subtitle: 'End-to-end AI-powered veterinary workflow — from first call to CRM record',
      steps: [
        { title: 'Incoming Call', desc: 'Digital VoIP API captures client calls with automatic routing and priority triage.', detail: 'Integrates with existing clinic phone systems. Supports multiple languages and automatic caller identification.' },
        { title: 'Voice Transcription', desc: 'Advanced voice transcription converts speech to text with veterinary-specific terminology support.', detail: 'Tuned for veterinary terminology. Real-time transcription with high accuracy on medical terms in pilot use.' },
        { title: 'History Collection', desc: 'Messenger app bot (Telegram/WhatsApp) collects pet history, symptoms, and owner observations before the visit.', detail: 'Structured questionnaires adapt based on species, breed, and reported symptoms. Available 24/7.' },
        { title: 'Symptom Analysis', desc: 'AI assistant reviews and cross-references symptoms against veterinary knowledge bases.', detail: 'Analyzes patterns across thousands of cases. Identifies rare conditions that might be missed in routine exams.' },
        { title: 'Clinical Support', desc: 'Suggests differential considerations, treatment options, and evidence-based recommendations for clinician review.', detail: 'Provides supporting information, alternative considerations, and recommended diagnostic tests. All outputs require veterinarian review and approval.' },
        { title: 'CRM Integration', desc: 'Automatic record creation in DrCloude/Vetmanager with structured clinical notes.', detail: 'Zero manual data entry. Complete visit records generated automatically with all findings and recommendations.' },
      ],
      timeline: 'Global Scientific Validation & Development Roadmap',
      timelineItems: [
        { date: 'June 2025 — Completed ✓', label: 'AVMA Convention 2025, Washington DC (Peer-reviewed scientific presentation)', done: true },
        { date: 'September 2025 — Completed ✓', label: 'FECAVA EuroCongress, Antwerp, Belgium (Poster presentation)', done: true },
        { date: 'April 22, 2026 — Completed ✓', label: 'WVAC 2026, Tokyo, Japan (Oral presentation, Program O4-3-7, Hall B7-2, 14:00 JST)', done: true },
        { date: 'Q3 2026 — In Progress ⟳', label: 'AI Model Development & Innovation Lab', done: false },
        { date: 'Q4 2026 — Upcoming', label: 'CRM Integration & Automatic Medical Records in Ukraine', done: false },
        { date: '2027 — Upcoming', label: 'Global Scaling', done: false },
      ],
      poweredBy: 'Built on modern AI infrastructure with multi-model support.',
      fecava: {
        title: 'FECAVA 2025 Poster Presentation',
        subtitle: 'Poster presented by Andrii Klietsov & Nataliia Klietsova at FECAVA EuroCongress, Antwerp, Belgium — September 2025',
        clickToEnlarge: 'Click to view full size',
      },
    },
    wvac: {
      badge: 'World Veterinary Congress 2026',
      title: 'Presenting at WVAC 2026 — Tokyo, Japan',
      subtitle: 'We are honored to present Dr.Vet AI at the World Veterinary Association Congress 2026 — the most prestigious and influential event in global veterinary medicine, uniting professionals, researchers, and industry leaders from around the world.',
      date: '21–24 April 2026 · Tokyo, Japan',
      linkLabel: 'Visit Official WVAC 2026 Website',
      linkUrl: 'https://wvac2026-tokyo.com',
    },
    features: {
      badge: 'Capabilities',
      title: 'Built for the real clinical day',
      items: [
        { title: 'Designed for unreliable conditions', desc: 'Offline templates and Voice AI keep the workflow running through power outages and connectivity disruption.' },
        { title: 'Privacy by design', desc: 'Aligned with GDPR principles and Ukrainian DIBAR data-protection rules — data minimisation, purpose limitation, no unauthorised sharing.' },
        { title: 'Built to scale across clinics', desc: 'Modular architecture and messenger-first delivery (Telegram, WhatsApp, Signal) — minimal new software to install.' },
        { title: 'Validated in real clinical use', desc: 'Refined inside three operating veterinary clinics over three years of continuous practice.' },
      ],
    },
    founders: {
      badge: 'Leadership',
      title: 'Meet the Founders',
      subtitle: 'Research-backed leadership with frontline veterinary and academic expertise',
      andrii: {
        name: 'Andrii Klietsov',
        role: 'Co-Founder & DVM',
        bio: 'DVM, Head of the Board of NGO "Society of Veterinary Business Owners" since 2020. Co-owner of the VetDopomoga clinics in Sumy. Graduate of Sumy National Agrarian University (2003), Master\'s degree (2007). Was in occupied Sumy during the full-scale invasion, personally delivered 3.6 tons of humanitarian aid and launched the #SavePets_SaveVets initiative — providing 7,416 free sterilizations. Representative of Ukraine at the FVE/UEVP/FECAVA and WVA assemblies.',
        linkLabel: 'Official Profile — UACBBC',
      },
      nataliia: {
        name: 'Nataliia Klietsova',
        role: 'Co-Founder & PhD Researcher',
        bio: 'PhD, Senior Lecturer, University of Hertfordshire, UK, and Sumy National Agrarian University, Ukraine, author of 58 peer-reviewed articles. Specializes in the digital transformation of Ukrainian SMEs during the war, social responsibility strategies, and international recruitment in the digital environment. Her academic work provides the scientific basis for Dr.Vet\'s approach to AI-based veterinary innovation.',
        linkLabel: 'Research Profile — University of Hertfordshire',
      },
    },
    video: {
      badge: 'Scientific Validation',
      title: 'Peer-Reviewed Presentation',
      subtitle: 'Watch our peer-reviewed presentation at AVMA Congress, Washington DC — independent scientific validation of Dr.Vet\'s approach.',
      caption: 'Scientific validation — Peer-reviewed presentation at AVMA Congress, Washington DC',
    },
    invest: {
      badge: 'Opportunity',
      title: 'Why Invest in Dr.Vet Now?',
      description: 'We are raising investment to launch the full production version of Dr.Vet. The funding will enable automatic medical record entries (replacing the current manual mode under human control), global scaling, and full integration with veterinary CRM systems. This investment will help stop pet-owner migration from Ukraine and create a scientific precedent for AI-driven veterinary innovations in the most turbulent environments where the profession is critically short-staffed.',
      items: [
        { title: 'Massive Market Gap', desc: 'The global veterinary AI market is nascent with enormous growth potential. Early movers capture disproportionate market share.' },
        { title: 'Proven Under Pressure', desc: 'No competitor has been battle-tested like Dr.Vet. Performance in Ukraine\'s extreme conditions validates reliability for any market.' },
        { title: 'WVA 2026 Showcase', desc: 'Presenting at the World Veterinary Association Congress in Tokyo, Japan (21–24 April 2026) — direct access to global veterinary industry decision-makers.' },
        { title: 'Scalable & High-Margin', desc: 'Clear path to international scaling through licensing model. Low marginal cost, high recurring revenue potential.' },
      ],
    },
    contact: {
      badge: 'Get Involved',
      title: 'Talk to the Dr.Vet team',
      name: 'Full Name',
      company: 'Company / Organization',
      email: 'Email Address',
      interest: 'I\'m Interested In',
      interestOptions: [
        'Book an online demo',
        'Partnership for my clinic / network',
        'Invite you as a speaker',
        'Investment discussion',
        'Book a meeting (until April 26, 2026 in Tokyo)',
      ],
      message: 'Message',
      submit: 'Send Inquiry',
      theme: 'Navigating Turbulent Environments: AI Revolution in Veterinary Clinics',
      themeSubtitle: 'A Case Study of VetDopomoga, Sumy, Ukraine',
      themeDate: 'April 22, 2026 · 14:00 JST',
      themeLocation: 'Program O4-3-7 · Hall B7-2, Tokyo International Forum',
      messagePlaceholder: 'Tell us about your company and what makes you interesting to us — your clinic or network, country, scale of operations, and the value you can bring.',
    },
    footer: {
      tagline: 'Veterinary AI, refined inside real clinics — from Ukraine to the world.',
      rights: '© 2026 Dr.Vet. All rights reserved.',
      wva: 'Presenting at WVA Congress 2026 — Tokyo, Japan (21–24 April)',
    },
    theme: {
      light: 'Light',
      dark: 'Dark',
    },
  },
  uk: {
    nav: {
      story: 'Історія',
      howItWorks: 'Як це працює',
      features: 'Можливості',
      invest: 'Інвестиції',
      contact: 'Контакт',
      impact: 'Вплив',
    },
    hero: {
      label: 'Ветеринарна AI-платформа',
      headline: 'AI-інфраструктура для реальних ветеринарних клінік',
      subtitle: 'Dr.Vet допомагає ветеринарним командам зменшувати навантаження від документації, структурувати клінічні робочі процеси та підтримувати кращі рішення у щоденній практиці. Розроблено в діючих клініках та відточено у вимогливих умовах.',
      cta: 'Дослідити платформу',
      ctaSecondary: 'Як це працює',
      proof: 'Перевірено в реальній ветеринарній практиці з 2022 року.',
    },
    story: {
      badge: 'Реальний кейс',
      title: 'Клінічний кейс: ВетДопомога',
      subtitle: 'Клініки «ВетДопомога», Сумська область, Україна — 2022–2025',
      p1: 'Клініки «ВетДопомога» в Сумській області стали первинним реальним середовищем, де тестувалися та відточувалися ранні робочі процеси Dr.Vet. Кейс демонструє, як AI-підтримані інструменти покращують послідовність документації та операційну стійкість у вимогливих клінічних умовах.',
      p2: 'Незважаючи на міграцію, відключення електроенергії та операційні виклики, клініки продовжували надавати допомогу.',
      p3: 'До 2025 року, з AI-асистентом Dr.Vet, час документації скоротився на 15%, ретельність оглядів зросла на 40%, а клініки досягли повного операційного відновлення.',
      outcomesLabel: 'Результати кейсу',
      chartVisits: 'Відновлення відвідувань клінік (2022–2025)',
      chartSales: 'Відновлення доходів (2022–2025)',
      chartMap: 'Сумська область — зона бойових дій та карта відновлення',
      stat1: '15%',
      stat1Label: 'Швидша документація',
      stat2: '40%',
      stat2Label: 'Ретельніші огляди',
      stat3: '100%',
      stat3Label: 'Повне операційне відновлення попри воєнні умови',
    },
    flowchart: {
      badge: 'Технологія',
      title: 'Як працює Dr.Vet',
      subtitle: 'Повний AI-робочий процес — від першого дзвінка до запису в CRM',
      steps: [
        { title: 'Вхідний дзвінок', desc: 'Цифровий VoIP API фіксує дзвінки клієнтів з автоматичною маршрутизацією та пріоритизацією.', detail: 'Інтеграція з існуючими телефонними системами клінік. Підтримка кількох мов та автоматична ідентифікація абонента.' },
        { title: 'Транскрипція голосу', desc: 'Сучасна система розпізнавання мовлення перетворює мовлення в текст із підтримкою ветеринарної термінології.', detail: 'Налаштовано під ветеринарну термінологію. Транскрипція в реальному часі з високою точністю на медичних термінах у пілотному використанні.' },
        { title: 'Збір анамнезу', desc: 'Бот месенджера (Telegram/WhatsApp) збирає історію хвороби тварини, симптоми та спостереження власника до візиту.', detail: 'Структуровані анкети адаптуються залежно від виду, породи та зареєстрованих симптомів. Доступно цілодобово.' },
        { title: 'Аналіз симптомів', desc: 'AI-асистент аналізує та перехресно перевіряє симптоми з базами ветеринарних знань.', detail: 'Аналізує закономірності серед тисяч випадків. Виявляє рідкісні стани, які можуть бути пропущені при стандартному огляді.' },
        { title: 'Клінічна підтримка', desc: 'Пропонує диференційні варіанти, опції лікування та рекомендації на основі доказової медицини для розгляду лікарем.', detail: 'Надає допоміжну інформацію, альтернативні варіанти та рекомендовані діагностичні тести. Усі рекомендації потребують перегляду та підтвердження ветеринарним лікарем.' },
        { title: 'Інтеграція з CRM', desc: 'Автоматичне створення записів у DrCloude/Vetmanager зі структурованими клінічними нотатками.', detail: 'Нуль ручного введення даних. Повні записи візитів генеруються автоматично з усіма висновками та рекомендаціями.' },
      ],
      timeline: 'Global Scientific Validation & Development Roadmap',
      timelineItems: [
        { date: 'June 2025 — Completed ✓', label: 'AVMA Convention 2025, Washington DC (Peer-reviewed scientific presentation)', done: true },
        { date: 'September 2025 — Completed ✓', label: 'FECAVA EuroCongress, Antwerp, Belgium (Poster presentation)', done: true },
        { date: 'April 22, 2026 — Completed ✓', label: 'WVAC 2026, Tokyo, Japan (Oral presentation, Program O4-3-7, Hall B7-2, 14:00 JST)', done: true },
        { date: 'Q3 2026 — In Progress ⟳', label: 'AI Model Development & Innovation Lab', done: false },
        { date: 'Q4 2026 — Upcoming', label: 'CRM Integration & Automatic Medical Records in Ukraine', done: false },
        { date: '2027 — Upcoming', label: 'Global Scaling', done: false },
      ],
      poweredBy: 'Побудовано на сучасній AI-інфраструктурі з підтримкою кількох моделей.',
      fecava: {
        title: 'Постерна презентація FECAVA 2025',
        subtitle: 'Постер представлений Андрієм Клєцовим та Наталією Клєцовою на FECAVA EuroCongress, Антверпен, Бельгія — вересень 2025',
        clickToEnlarge: 'Натисніть для перегляду в повному розмірі',
      },
    },
    wvac: {
      badge: 'Всесвітній ветеринарний конгрес 2026',
      title: 'Доповідь на WVAC 2026 — Токіо, Японія',
      subtitle: 'Для нас велика честь представити Dr.Vet AI на Всесвітньому ветеринарному конгресі 2026 — найпрестижнішому та найвпливовішому заході у світовій ветеринарній медицині, що об\'єднує професіоналів, науковців та лідерів галузі з усього світу.',
      date: '21–24 квітня 2026 · Токіо, Японія',
      linkLabel: 'Відвідати офіційний сайт WVAC 2026',
      linkUrl: 'https://wvac2026-tokyo.com',
    },
    features: {
      badge: 'Можливості',
      title: 'Створено для реального клінічного дня',
      items: [
        { title: 'Розраховано на нестабільні умови', desc: 'Офлайн-шаблони та голосовий AI підтримують робочий процес під час відключень електроенергії та збоїв зв’язку.' },
        { title: 'Приватність за замовчуванням', desc: 'Відповідно до принципів GDPR та українських правил захисту даних ДІБАР — мінімізація даних, обмеження мети, без несанкціонованого обміну.' },
        { title: 'Створено для масштабування на багато клінік', desc: 'Модульна архітектура та доставка через месенджери (Telegram, WhatsApp, Signal) — мінімум нового ПЗ для встановлення.' },
        { title: 'Перевірено в реальній клінічній роботі', desc: 'Відточено всередині трьох діючих ветеринарних клінік протягом трьох років безперервної практики.' },
      ],
    },
    founders: {
      badge: 'Керівництво',
      title: 'Познайомтесь із засновниками',
      subtitle: 'Науково обґрунтоване лідерство з практичним досвідом на передовій ветеринарії та в академічному середовищі',
      andrii: {
        name: 'Андрій Клєцов',
        role: 'Співзасновник, лікар ветеринарної медицини',
        bio: 'Лікар ветеринарної медицини, голова правління ГО «Спілка власників ветеринарного бізнесу» з 2020 року. Співвласник клінік «ВетДопомога» у Сумах. Випускник Сумського національного аграрного університету (2003), магістр (2007). Перебував в окупованих Сумах під час повномасштабного вторгнення, особисто доставив 3,6 тонни гуманітарної допомоги та запустив ініціативу #SavePets_SaveVets — 7 416 безкоштовних стерилізацій. Представник України на асамблеях FVE/UEVP/FECAVA та WVA.',
        linkLabel: 'Офіційний профіль — UACBBC',
      },
      nataliia: {
        name: 'Наталія Клєцова',
        role: 'Співзасновник, PhD, науковий дослідник',
        bio: 'PhD, старший викладач Університету Хартфордшира (Великобританія) та Сумського національного аграрного університету (Україна), авторка 58 рецензованих наукових статей. Спеціалізується на цифровій трансформації українських МСП під час війни, стратегіях соціальної відповідальності та міжнародному рекрутингу в цифровому середовищі. Її академічна робота забезпечує наукову основу підходу Dr.Vet до AI-інновацій у ветеринарії.',
        linkLabel: 'Дослідницький профіль — Університет Хартфордшира',
      },
    },
    video: {
      badge: 'Наукова валідація',
      title: 'Рецензована презентація',
      subtitle: 'Перегляньте нашу рецензовану презентацію на Конгресі AVMA у Вашингтоні — незалежна наукова валідація підходу Dr.Vet.',
      caption: 'Наукова валідація — рецензована презентація на Конгресі AVMA, Вашингтон',
    },
    invest: {
      badge: 'Інвестиційна можливість',
      title: 'Чому інвестувати в Dr.Vet саме зараз?',
      description: 'Ми залучаємо інвестиції для запуску повної виробничої версії Dr.Vet. Ці кошти забезпечать автоматичне внесення медичних записів (замість поточного ручного режиму під контролем людини), глобальне масштабування та повну інтеграцію з ветеринарними CRM-системами. Ці інвестиції допоможуть зупинити міграцію власників домашніх тварин з України та створять науковий прецедент для AI-інновацій у ветеринарії в найбільш турбулентних середовищах, де професія критично недоукомплектована.',
      items: [
        { title: 'Величезний ринковий розрив', desc: 'Глобальний ринок ветеринарного AI знаходиться на початковій стадії з величезним потенціалом зростання. Ранні гравці захоплюють непропорційну частку ринку.' },
        { title: 'Випробувано під тиском', desc: 'Жоден конкурент не пройшов такого бойового випробування, як Dr.Vet. Ефективність в екстремальних умовах України підтверджує надійність для будь-якого ринку.' },
        { title: 'Доповідь на WVAC 2026', desc: 'Виступ на Всесвітньому ветеринарному конгресі в Токіо, Японія (21–24 квітня 2026) — прямий доступ до світових лідерів ветеринарної галузі та прийняття рішень.' },
        { title: 'Масштабованість та висока маржинальність', desc: 'Чіткий шлях до міжнародного масштабування через ліцензійну модель. Низькі граничні витрати, високий потенціал регулярного доходу.' },
      ],
    },
    contact: {
      badge: 'Долучайтесь',
      title: 'Поспілкуйтеся з командою Dr.Vet',
      name: 'Повне ім\'я',
      company: 'Компанія / Організація',
      email: 'Електронна пошта',
      interest: 'Мене цікавить',
      interestOptions: [
        'Замовити демонстрацію online',
        'Партнерство для моєї клініки / мережі',
        'Запросити вас у вигляді доповідача',
        'Обговорення інвестицій',
        'Записатися на зустріч (включно до 26 квітня 2026 в Токіо)',
      ],
      message: 'Повідомлення',
      submit: 'Надіслати запит',
      theme: 'Навігація в турбулентних умовах: AI-революція у ветеринарних клініках',
      themeSubtitle: 'Кейс-стаді клінік «ВетДопомога», Суми, Україна',
      themeDate: '22 квітня 2026 · 14:00 за Токіо (JST)',
      themeLocation: 'Програма O4-3-7 · Зал B7-2, Tokyo International Forum',
      messagePlaceholder: 'Розкажіть про вашу компанію та чим ви можете нас зацікавити — клініка чи мережа, країна, масштаб, та яку цінність ви приносите.',
    },
    footer: {
      tagline: 'Ветеринарний AI, відточений усередині реальних клінік — з України у світ.',
      rights: '© 2026 Dr.Vet. Усі права захищені.',
      wva: 'Доповідь на Всесвітньому ветеринарному конгресі 2026 — Токіо, Японія (21–24 квітня)',
    },
    theme: {
      light: 'Світла',
      dark: 'Темна',
    },
  },
  ja: {
    nav: {
      story: 'ストーリー',
      howItWorks: '仕組み',
      features: '機能',
      invest: '投資',
      contact: 'お問い合わせ',
      impact: 'インパクト',
    },
    hero: {
      label: '獣医AIプラットフォーム',
      headline: '実際の獣医クリニックのためのAIインフラ',
      subtitle: 'Dr.Vetは、獣医チームの文書作成負担を軽減し、臨床ワークフローを構造化し、日々の診療における意思決定を支援します。稼働中のクリニックで開発され、厳しい条件下で改良されました。',
      cta: 'プラットフォームを見る',
      ctaSecondary: '仕組みを見る',
      proof: '2022年から実際の獣医診療現場で検証されています。',
    },
    story: {
      badge: '実地ケーススタディ',
      title: '臨床ケーススタディ：VetDopomoga',
      subtitle: 'VetDopomogaクリニック、ウクライナ・スームィ州 — 2022〜2025年',
      p1: 'スームィ州のVetDopomogaクリニックは、Dr.Vetの初期ワークフローが実地で検証・改良された最初の現場です。本ケースは、AI支援ツールが厳しい臨床環境において文書の一貫性と運営の回復力を向上できることを示しています。',
      p2: '人口流出、停電、運用上の課題にもかかわらず、クリニックは診療を継続しました。',
      p3: '2025年までに、Dr.VetのAI支援により、文書作成時間は15%短縮、検査の網羅性は40%向上し、クリニックは完全な運営回復を達成しました。',
      outcomesLabel: 'ケーススタディの成果',
      chartVisits: 'クリニック来院数の回復（2022–2025）',
      chartSales: '収益の回復（2022–2025）',
      chartMap: 'スームィ地域 — 戦闘地域と回復マップ',
      stat1: '15%',
      stat1Label: '文書作成の高速化',
      stat2: '40%',
      stat2Label: 'より徹底した検査',
      stat3: '100%',
      stat3Label: '戦時下での完全な運営回復',
    },
    flowchart: {
      badge: 'テクノロジー',
      title: 'Dr.Vetの仕組み',
      subtitle: '初回の電話からCRM記録まで — エンドツーエンドのAI獣医ワークフロー',
      steps: [
        { title: '着信受付', desc: 'デジタルVoIP APIがクライアントの電話を自動ルーティングと優先トリアージで処理します。', detail: '既存のクリニック電話システムとシームレスに統合。多言語対応と発信者自動識別機能を搭載。' },
        { title: '音声文字起こし', desc: '高度な音声認識が獣医学用語に対応した形で音声をテキストに変換します。', detail: '獣医学用語向けにチューニング済み。パイロット運用では医学用語に対して高い精度のリアルタイム文字起こしを実現。' },
        { title: '病歴収集', desc: 'メッセンジャーアプリのボット（Telegram/WhatsApp）が来院前にペットの病歴、症状、飼い主の観察事項を収集。', detail: '動物種・品種・報告症状に基づく適応型構造化アンケート。24時間365日利用可能。' },
        { title: '症状分析', desc: 'AIアシスタントが獣医学知識データベースと症状を照合・分析します。', detail: '数千のケースにわたるパターンを分析。通常の検査では見逃されがちな希少疾患も特定。' },
        { title: '臨床サポート', desc: '獣医師の判断のために、鑑別候補、治療オプション、エビデンスに基づく推奨事項を提示します。', detail: '補足情報、代替候補、推奨検査を提示。すべての出力は獣医師による確認と承認が必要です。' },
        { title: 'CRM統合', desc: 'DrCloude/Vetmanagerに構造化された臨床記録を自動生成・登録。', detail: '手動データ入力完全不要。すべての所見と推奨事項を含む完全な診療記録を自動生成。' },
      ],
      timeline: 'Global Scientific Validation & Development Roadmap',
      timelineItems: [
        { date: 'June 2025 — Completed ✓', label: 'AVMA Convention 2025, Washington DC (Peer-reviewed scientific presentation)', done: true },
        { date: 'September 2025 — Completed ✓', label: 'FECAVA EuroCongress, Antwerp, Belgium (Poster presentation)', done: true },
        { date: 'April 22, 2026 — Completed ✓', label: 'WVAC 2026, Tokyo, Japan (Oral presentation, Program O4-3-7, Hall B7-2, 14:00 JST)', done: true },
        { date: 'Q3 2026 — In Progress ⟳', label: 'AI Model Development & Innovation Lab', done: false },
        { date: 'Q4 2026 — Upcoming', label: 'CRM Integration & Automatic Medical Records in Ukraine', done: false },
        { date: '2027 — Upcoming', label: 'Global Scaling', done: false },
      ],
      poweredBy: '最新のAIインフラと複数モデル対応で構築。',
      fecava: {
        title: 'FECAVA 2025 ポスター発表',
        subtitle: 'アンドリー・クリエツォフ博士とナタリア・クリエツォワ博士によるFECAVA欧州大会（アントワープ、ベルギー）でのポスター発表 — 2025年9月',
        clickToEnlarge: 'クリックして拡大表示',
      },
    },
    wvac: {
      badge: '世界獣医師会大会 2026',
      title: 'WVAC 2026で発表 — 東京',
      subtitle: 'Dr.Vet AIを世界獣医師会大会（WVAC）2026で発表できることを大変光栄に存じます。WVACは世界の獣医学において最も権威ある最高峰の国際会議であり、世界中から獣医師、研究者、業界のリーダーが一堂に会します。',
      date: '2026年4月21日〜24日 · 東京',
      linkLabel: 'WVAC 2026 公式サイトへ',
      linkUrl: 'https://wvac2026-tokyo.com',
    },
    features: {
      badge: '機能と特長',
      title: '実際の臨床現場のために設計',
      items: [
        { title: '不安定な環境向けに設計', desc: 'オフラインテンプレートと音声AIにより、停電や通信障害が発生してもワークフローを継続。' },
        { title: 'プライバシー設計', desc: 'GDPR原則およびウクライナのDIBARデータ保護規則に準拠 — データ最小化、目的制限、無許可共有なし。' },
        { title: '多くのクリニックへ拡張可能', desc: 'モジュラーアーキテクチャとメッセンジャー（Telegram、WhatsApp、Signal）中心の提供 — 新規導入ソフトウェアを最小化。' },
        { title: '実臨床での検証済み', desc: '稼働中の3つの獣医クリニックで3年間にわたる継続的な診療の中で改良。' },
      ],
    },
    founders: {
      badge: 'リーダーシップ',
      title: '創設者のご紹介',
      subtitle: '最前線の獣医学と学術研究に裏打ちされた、科学的根拠に基づくリーダーシップ',
      andrii: {
        name: 'アンドリー・クリエツォフ',
        role: '共同創設者・獣医師（DVM）',
        bio: '獣医師（DVM）、2020年より公益団体「獣医ビジネスオーナー協会」理事長。スームィ市のVetDopomogaクリニック共同オーナー。スームィ国立農業大学卒業（2003年）、修士号取得（2007年）。全面侵攻中も占領下のスームィに留まり、3.6トンの人道支援物資を自ら配達、#SavePets_SaveVetsイニシアチブを立ち上げ7,416件の無料不妊手術を実施。FVE/UEVP/FECAVAおよびWVA総会におけるウクライナ代表。',
        linkLabel: '公式プロフィール — UACBBC',
      },
      nataliia: {
        name: 'ナタリア・クリエツォワ',
        role: '共同創設者・PhD研究者',
        bio: 'PhD、シニアレクチャラー、ハートフォードシャー大学（英国）およびスームィ国立農業大学（ウクライナ）、査読付き論文58本の著者。戦時下のウクライナ中小企業のデジタルトランスフォーメーション、社会的責任戦略、デジタル環境における国際人材採用を専門とする。その学術研究がDr.VetのAIベース獣医イノベーションの科学的基盤を提供。',
        linkLabel: '研究者プロフィール — ハートフォードシャー大学',
      },
    },
    video: {
      badge: '科学的検証',
      title: '査読付き学術発表',
      subtitle: 'ワシントンDCのAVMA大会での査読付き発表をご覧ください — Dr.Vetのアプローチに対する独立した科学的検証です。',
      caption: '科学的検証 — AVMA大会での査読付き発表（ワシントンDC）',
    },
    invest: {
      badge: '投資機会',
      title: 'なぜ今こそDr.Vetに投資すべきか',
      description: 'Dr.Vetの完全製品版ローンチに向けて投資を募集しています。この資金により、自動医療記録入力（現行の人的管理下の手動モードからの移行）、グローバル展開、獣医CRMシステムとの完全統合が実現します。この投資はウクライナからのペットオーナー流出を防ぎ、専門職が深刻に不足する最も困難な環境におけるAI獣医イノベーションの科学的先例を確立します。',
      items: [
        { title: '巨大な市場機会', desc: 'グローバル獣医AIマーケットは黎明期にあり、膨大な成長ポテンシャルを秘めています。先行者が圧倒的な市場シェアを獲得します。' },
        { title: '極限環境での実証', desc: 'Dr.Vetほど実戦で検証された競合は存在しません。ウクライナの極限環境での実績が、あらゆる市場での信頼性を証明しています。' },
        { title: 'WVAC 2026での公式発表', desc: '世界獣医師会大会（東京、2026年4月21〜24日）で発表 — 世界の獣医業界の意思決定者に直接アクセスする絶好の機会。' },
        { title: '高い拡張性と収益性', desc: 'ライセンスモデルによる国際展開への明確なロードマップ。低い限界費用と高い継続収益ポテンシャル。' },
      ],
    },
    contact: {
      badge: 'ご参加ください',
      title: 'Dr.Vetチームへお問い合わせ',
      name: 'お名前',
      company: '会社名 / 組織名',
      email: 'メールアドレス',
      interest: 'ご関心のある分野',
      interestOptions: [
        'オンラインデモを予約',
        'クリニック / ネットワークでの導入パートナーシップ',
        '講演者としてお招きしたい',
        '投資に関するご相談',
        'ミーティングを予約（2026年4月26日まで・東京）',
      ],
      message: 'メッセージ',
      submit: 'お問い合わせを送信',
      theme: '激動の環境を乗り越える：獣医クリニックにおけるAI革命',
      themeSubtitle: 'ウクライナ・スームィ「VetDopomoga」クリニックのケーススタディ',
      themeDate: '2026年4月22日 · 14:00 JST',
      themeLocation: 'プログラム O4-3-7 · ホール B7-2、東京国際フォーラム',
      messagePlaceholder: '貴社についてご紹介ください — クリニックまたはネットワーク、国、規模、そして私たちにとって魅力となる価値をお聞かせください。',
    },
    footer: {
      tagline: '実際のクリニックの中で磨かれた獣医AI — ウクライナから世界へ。',
      rights: '© 2026 Dr.Vet. All rights reserved.',
      wva: '世界獣医師会大会 2026で発表 — 東京（4月21〜24日）',
    },
    theme: {
      light: 'ライト',
      dark: 'ダーク',
    },
  },
} as const;
